import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Sparkles, CalendarDays, Camera, Wallet, KeyRound, Boxes } from "lucide-react";

const features = [
  { icon: CalendarDays, title: "Airbnb & VRBO sync", desc: "Auto-create turnover jobs from your iCal calendars." },
  { icon: Camera, title: "Photo proof", desc: "After-service & damage photos, per-room checklists." },
  { icon: KeyRound, title: "Door codes & access", desc: "Give cleaners exactly what they need on site." },
  { icon: Boxes, title: "Inventory tracking", desc: "Low-stock alerts so supplies never run out." },
  { icon: Wallet, title: "Cleaner payouts", desc: "Auto-split pay, CSV & branded PDF statements." },
  { icon: Sparkles, title: "Roles & team", desc: "Admins and cleaners with the right access." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="SmartHost" className="h-9 w-9 object-contain" />
            <span className="font-display text-xl font-bold">SmartHost Solutions</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-login">Sign in</Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="nav-signup">Start trial</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm text-secondary-foreground mb-6">
          <Sparkles className="h-4 w-4" /> Built for short-term rental turnover teams
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl mx-auto">
          Every turnover, spotless and accounted for.
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-2xl mx-auto leading-relaxed">
          Sync Airbnb & VRBO calendars, dispatch cleaners with photos, door codes and
          inventory, and pay your team — all in one place.
        </p>
        <div className="flex items-center justify-center gap-3 mt-8">
          <Link to="/signup">
            <Button size="lg" className="h-12 px-7 text-base" data-testid="hero-cta">
              Start free trial
            </Button>
          </Link>
          <Link to="/login">
            <Button size="lg" variant="outline" className="h-12 px-7 text-base">
              Sign in
            </Button>
          </Link>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Free 3-day trial · from $39/mo · cancel anytime</p>
        <div className="mt-6">
          <Link to="/demo" className="text-sm text-primary hover:underline font-medium" data-testid="hero-demo-link">
            Or watch a 30-second product demo →
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <Card key={f.title} className="p-6">
              <f.icon className="h-6 w-6 text-primary mb-3" strokeWidth={1.7} />
              <h3 className="font-display font-semibold text-lg">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl font-semibold text-center tracking-tight">
          Simple, usage-based pricing
        </h2>
        <p className="text-muted-foreground text-center mt-2">
          Two plans. Free 3-day trial on both. Only pay for the properties you manage.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-6 items-start">
          {/* Starter */}
          <Card className="p-8 relative overflow-hidden flex flex-col h-full" data-testid="pricing-card-starter">
            <span className="text-xs font-semibold tracking-[0.12em] uppercase text-muted-foreground">
              Starter
            </span>
            <div className="flex items-end gap-2 mt-3">
              <span className="font-display text-5xl font-semibold">$39</span>
              <span className="text-muted-foreground mb-2">/month base</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Everything to run day-to-day cleaning operations.
            </p>
            <p className="text-xs text-muted-foreground mt-2" data-testid="starter-properties-note">
              Includes 3 properties. Additional properties: $10/month each.
            </p>
            <div className="my-6 h-px bg-border" />
            <ul className="space-y-3 text-sm flex-1">
              {[
                "Airbnb + VRBO calendar sync",
                "Unlimited cleaners & admins",
                "Photos, door codes, inventory & payouts",
                "Basic invoicing (PDFs, mark paid)",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup?plan=starter" className="block mt-8">
              <Button variant="outline" className="w-full h-12 text-base" data-testid="pricing-cta-starter">
                Start free trial
              </Button>
            </Link>
          </Card>

          {/* Premium */}
          <Card
            className="p-8 relative overflow-hidden flex flex-col h-full border-2 border-primary shadow-md"
            data-testid="pricing-card-premium"
          >
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-semibold px-4 py-1 rounded-bl-xl">
              Most popular
            </div>
            <span className="text-xs font-semibold tracking-[0.12em] uppercase text-primary">
              Premium
            </span>
            <div className="flex items-end gap-2 mt-3">
              <span className="font-display text-5xl font-semibold">$69</span>
              <span className="text-muted-foreground mb-2">/month base</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Grow revenue with branding, automation & AI.
            </p>
            <p className="text-xs text-muted-foreground mt-2" data-testid="premium-properties-note">
              Includes 3 properties. Additional properties: $10/month each.
            </p>
            <div className="my-6 h-px bg-border" />
            <ul className="space-y-3 text-sm flex-1">
              {[
                "Everything in Starter",
                "Custom invoice & contract branding",
                "Automatic payment reminders & late fees",
                "AI Estimate Calculator",
                "Lead capture (CRM)",
                "Contracts & e-signatures",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup?plan=premium" className="block mt-8">
              <Button className="w-full h-12 text-base" data-testid="pricing-cta-premium">
                Start free trial
              </Button>
            </Link>
          </Card>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Both plans include 3 properties. Additional properties: $10/month each. Cancel anytime.
        </p>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        <p>Built by a real Airbnb management &amp; cleaning company.</p>
        <p className="mt-1">© {new Date().getFullYear()} SmartHost Solutions · Smart Systems. Seamless Stays.</p>
      </footer>
    </div>
  );
}
